import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { CheckboxGroup } from "@/components/elements/CheckboxGroup";
import { Select } from "@/components/elements/Select";
import { Switch } from "@/components/elements/Switch";
import { Grid, Stack, Typography } from "@mui/material";
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { TLearnerForm } from "@/features/learner/types/learnerForm";
import { LearnerFormTitle } from "@/features/learner/components/learner-form/LearnerFormTitle";
import { state } from "@/theme/colors";
import { calculateAvailableLearningWeeks, getNextOrCurrentMonday } from "@/features/learner/utils/calculates";
import { IconWarning } from "@/assets/icons";
import { TCheckboxOption } from "@/components/elements/CheckboxGroup";
import { useEnum } from "@/api/services/global/enum";
import { TIndustryExam, TIndustrySubject } from "@/api/services/main/company/dtos/get-company-list.dto";
import { CurriculumScheduleTable } from "@/features/learner/components/CurriculumScheduleTable";
import { TOption } from "@/types/common";
import { EXAM_METHOD, EXAM_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { MOCK_EXAM_WEEKS } from "@/constants/common";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { EMPTY_STR } from "@/constants/string";
import { useProcedureManagementToggle } from "@/features/learner/hook/useProcedureManagementToggle";

type TLearnerCurriculumProps = {
  subjectCount: number | null;
  hasSubjects: boolean;
  subjects: TIndustrySubject[];
  mockExamQuestionCount?: number | null;
  industryPartTypes: string[];
  industryExams: TIndustryExam[];
  hasIndustry: boolean;
  // True while the selected industry's company data is still loading. Used to avoid
  // forcing isUsingLearningApp OFF before authoritative subject data is available
  // (which would otherwise flip the value on mount and mark the form dirty).
  isLoadingIndustry: boolean;
  // True when the learner already has an issued (active) learning-app account. Turning the
  // toggle OFF in this case requires a confirmation since it suspends the account.
  hasIssuedAccount: boolean;
  // Persisted 手続ステータス — read-only, used solely to gate the procedure-management OFF confirm modal.
  procedureStatusKey: string | null;
};

export const LearnerCurriculumForm: FC<TLearnerCurriculumProps> = ({
  subjectCount,
  hasSubjects,
  subjects,
  mockExamQuestionCount,
  industryPartTypes,
  industryExams,
  hasIndustry,
  isLoadingIndustry,
  hasIssuedAccount,
  procedureStatusKey,
}) => {
  const { control, setValue } = useFormContext<TLearnerForm>();
  const { learnerId } = useParams();
  const { data: allPartTypeOptions } = useEnum("EPartType");

  const learningStartDate = useWatch({ control, name: "learnerPlan.learningStartDate" });
  const examScheduledYearMonth = useWatch({ control, name: "learnerPlan.examScheduledYearMonth" });
  const examId = useWatch({ control, name: "learnerPlan.examId" });
  const industryId = useWatch({ control, name: "industryId" });
  const selectedPartTypes = useWatch({ control, name: "learnerPlan.partTypes" });
  const isUsingLearningApp = useWatch({ control, name: "isUsingLearningApp" });
  const isLearningAppOff = isUsingLearningApp === false;

  // Filter subjects by selected partTypes (null → all subjects). Mirrors CreateLearnerPlanModal.
  const filteredSubjects = useMemo(() => {
    if (!subjects.length) return [];
    if (!selectedPartTypes) return subjects;
    return subjects.filter((s) => selectedPartTypes.includes(s.partType));
  }, [subjects, selectedPartTypes]);

  // Schedule table shows on both create and edit screens, when the learning app is ON and the industry actually has matching subjects.
  const showScheduleTable = !isLearningAppOff && filteredSubjects.length > 0;

  // Show all partType options; disable ones not in selected industry
  const hasPartTypes = industryPartTypes.length > 0;
  const partTypeOptions: TCheckboxOption[] = useMemo(
    () => allPartTypeOptions.filter((p) => industryPartTypes.includes(String(p.value))).map((p) => ({ value: p.value, label: p.label + "パート" })),
    [allPartTypeOptions, industryPartTypes],
  );

  // Set default partTypes only when user changes industry (industryId change)
  const prevIndustryId = useRef(industryId);
  useEffect(() => {
    if (prevIndustryId.current === industryId) return;
    prevIndustryId.current = industryId;
    setValue("learnerPlan.partTypes", industryPartTypes.length > 0 ? industryPartTypes : null);
  }, [industryId, industryPartTypes, setValue]);

  // Industry with no subjects can't use the learning app: force OFF and lock the toggle.
  // Toggle is only changeable when the selected industry has subjects.
  const isLearningAppLocked = !hasSubjects;

  // Default isUsingLearningApp on industry change: ON if industry has subjects, OFF otherwise.
  // Use `hasSubjects` (not the part-filtered `subjectCount`): on the render where industry changes,
  // partTypes are still stale so `subjectCount` can be 0, while `hasSubjects` is already correct.
  const prevIndustryIdForApp = useRef(industryId);
  useEffect(() => {
    if (prevIndustryIdForApp.current === industryId) return;
    prevIndustryIdForApp.current = industryId;
    setValue("isUsingLearningApp", hasSubjects);
  }, [industryId, hasSubjects, setValue]);

  // Keep OFF whenever the industry has no subjects (covers mount/edit, not just industry change).
  // Wait until industry data has loaded; otherwise the transient "no subjects" state during
  // the company fetch would flip isUsingLearningApp OFF on mount and mark the form dirty.
  useEffect(() => {
    if (isLoadingIndustry) return;
    if (isLearningAppLocked && isUsingLearningApp !== false) {
      setValue("isUsingLearningApp", false);
    }
  }, [isLoadingIndustry, isLearningAppLocked, isUsingLearningApp, setValue]);

  // When the learning app is turned OFF, clear learningStartDate & partTypes (they are also disabled below).
  // When turned back ON, restore learningStartDate to the nearest Monday default and re-check the partTypes
  const prevUsingApp = useRef(isUsingLearningApp);
  useEffect(() => {
    const turnedOff = prevUsingApp.current !== false && isUsingLearningApp === false;
    const turnedOn = prevUsingApp.current === false && isUsingLearningApp === true;
    prevUsingApp.current = isUsingLearningApp;
    if (turnedOff) {
      setValue("learnerPlan.learningStartDate", null, { shouldValidate: true, shouldDirty: true });
      setValue("learnerPlan.partTypes", null, { shouldValidate: true, shouldDirty: true });
    } else if (turnedOn) {
      setValue("learnerPlan.learningStartDate", getNextOrCurrentMonday(), { shouldValidate: true, shouldDirty: true });
      setValue("learnerPlan.partTypes", industryPartTypes.length > 0 ? industryPartTypes : null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [isUsingLearningApp, industryPartTypes, setValue]);

  // Build exam select options from selected industry's exams.
  // Always include the currently selected exam regardless of status so edit mode
  // can render its label even if the saved exam is no longer ACTIVE.
  const examOptions: TOption[] = useMemo(
    () =>
      !hasIndustry
        ? []
        : industryExams
            .filter((e) => e.status === EXAM_STATUS.ACTIVE || String(e.id) === String(examId))
            .map((e) => ({
              value: e.id.toString(),
              label: e.periodName,
            })),
    [hasIndustry, industryExams, examId],
  );

  // Reset examId when industry changes (use industryId as change trigger, not deep-compare)
  const prevIndustryIdForExam = useRef(industryId);
  useEffect(() => {
    if (learnerId) return;
    if (prevIndustryIdForExam.current !== industryId) {
      setValue("learnerPlan.examId", "");
      prevIndustryIdForExam.current = industryId;
    }
  }, [industryId, learnerId, setValue]);

  // Check if selected exam is PAPER_TEST → lock examScheduledYearMonth to examMonth
  const selectedExam = useMemo(() => industryExams.find((e) => String(e.id) === String(examId)), [industryExams, examId]);
  const isPaperTest = selectedExam?.examMethod === EXAM_METHOD.PAPER_TEST;

  const prevExamId = useRef(examId);
  useEffect(() => {
    if (isPaperTest && selectedExam?.examMonth) {
      const isUserChange = prevExamId.current !== examId;
      setValue("learnerPlan.examScheduledYearMonth", dayjs(selectedExam.examMonth).startOf("month"), { shouldDirty: isUserChange });
    }
    prevExamId.current = examId;
  }, [isPaperTest, selectedExam, examId, setValue]);

  const availableWeeks = calculateAvailableLearningWeeks(learningStartDate, examScheduledYearMonth);
  const isWeeksInsufficient = availableWeeks !== null && subjectCount !== null && subjectCount + MOCK_EXAM_WEEKS > availableWeeks;

  // Confirm before turning the learning app OFF when an account has already been issued
  // (this suspends the account). Toggling ON, or OFF without an issued account, applies directly.
  const [isSuspendConfirmOpen, setIsSuspendConfirmOpen] = useState(false);

  const handleToggleLearningApp = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!checked && hasIssuedAccount) {
      setIsSuspendConfirmOpen(true);
      return;
    }
    setValue("isUsingLearningApp", checked, { shouldDirty: true, shouldValidate: true });
  };

  const handleConfirmSuspend = () => {
    setValue("isUsingLearningApp", false, { shouldDirty: true, shouldValidate: true });
    setIsSuspendConfirmOpen(false);
  };

  // 手続き管理を利用する toggle ↔ procedureStatusKey two-way sync.
  const {
    isProcedureManaged,
    requestToggle: requestProcedureToggle,
    isConfirmOpen: isProcedureConfirmOpen,
    closeConfirm: closeProcedureConfirm,
    confirmOff: confirmProcedureOff,
  } = useProcedureManagementToggle({ control, setValue, isCreate: !learnerId, procedureStatusKey });

  const handleToggleProcedure = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    requestProcedureToggle(checked);
  };

  return (
    <>
      <InnerBox title={<LearnerFormTitle text="受講カリキュラム" />} showDivider={false}>
        <Grid container rowSpacing={2} columnSpacing={3}>
          <Grid item xs={12}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="body14" mr={2}>
                手続き管理を利用する
              </Typography>
              <Switch nonControl checked={!!isProcedureManaged} onChange={handleToggleProcedure} />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Typography variant="body14" mr={2}>
                学習アプリを利用する
              </Typography>
              <Switch nonControl checked={!!isUsingLearningApp} disabled={isLearningAppLocked} onChange={handleToggleLearningApp} />
            </Stack>
          </Grid>
          <Grid item xs={6}>
            {isLearningAppOff ? (
              // Learning app OFF: show an empty readonly input (no YYYY/MM/DD date mask)
              <FormField label="カリキュラム開始日">
                <Input readonly value="" placeholder="" />
              </FormField>
            ) : (
              <FormField control={control} name="learnerPlan.learningStartDate" label="カリキュラム開始日" required>
                <InputDatePicker placeholder="2022/01/01" format="YYYY/MM/DD" />
              </FormField>
            )}
          </Grid>
          <Grid item xs={6}>
            {hasPartTypes && (
              <FormField control={control} name="learnerPlan.partTypes" label="受験・学習対象のパート" required={!isLearningAppOff}>
                <CheckboxGroup options={partTypeOptions} disabled={isLearningAppOff} flexDirection="row" />
              </FormField>
            )}
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="learnerPlan.examId" label="受験予定" required>
              <Select options={examOptions} readonly={!!learnerId} placeholder="選択してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="learnerPlan.examScheduledYearMonth" label="受験予定年月" required>
              <InputDatePicker
                views={["year", "month"]}
                openTo="month"
                format="YYYY/MM"
                placeholder="2027/01"
                readonly={!!learnerId || isPaperTest}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField label="大まかな学習可能な期間（週数）">
              <Input
                readonly
                value={isLearningAppOff ? EMPTY_STR.TEXT : availableWeeks !== null ? `${availableWeeks}週間` : "自動入力内容 (readonly)"}
              />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField label="学習に必要な期間（週数）">
              <Input
                readonly
                value={
                  isLearningAppOff
                    ? EMPTY_STR.TEXT
                    : subjectCount !== null
                      ? `${subjectCount + MOCK_EXAM_WEEKS}週間（${subjectCount} WEEK + 模擬試験）`
                      : "自動入力内容 (readonly)"
                }
              />
            </FormField>
          </Grid>
          {isWeeksInsufficient && (
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <IconWarning />
                <Typography variant="cap12" color={state.warning_1}>
                  受講可能な週数が科目数より少ないため、学習期間が不足する可能性があります。
                </Typography>
              </Stack>
            </Grid>
          )}
          {showScheduleTable && (
            <Grid item xs={12}>
              <CurriculumScheduleTable
                learningStartDate={learningStartDate}
                subjects={filteredSubjects}
                mockExamQuestionCount={mockExamQuestionCount}
              />
            </Grid>
          )}
          {/* TODO: 自習モード - temporarily hidden
        <Grid item xs={12}>
          <FormField control={control} name="learnerPlan.isSelfStudyMode" label="">
            <Checkbox label="自習モード" checked={!!isSelfStudyMode} />
            <Typography variant="cap12" color={text.secondary} ml={3.5}>学習者が自ら科目を選択して自立的に学習を進めるモードです。</Typography>
          </FormField>
        </Grid>
        */}
        </Grid>
      </InnerBox>
      <ConfirmSaveModal open={isSuspendConfirmOpen} onClose={() => setIsSuspendConfirmOpen(false)} onOk={handleConfirmSuspend}>
        <Stack mb={2}>
          <Typography>発行済みのアカウントが停止され、本人は学習アプリを利用できなくなりますがよろしいですか？</Typography>
        </Stack>
      </ConfirmSaveModal>
      <ConfirmSaveModal open={isProcedureConfirmOpen} onClose={closeProcedureConfirm} onOk={confirmProcedureOff}>
        <Stack mb={2}>
          <Typography>
            既に手続ステータスが記録されており、手続き管理を利用しない場合は手続ステータスが「管理対象外」に変更されます。よろしいですか？
          </Typography>
        </Stack>
      </ConfirmSaveModal>
    </>
  );
};
